import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';

import { opTime } from 'utils/dates';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';
import { useUrlSearchParams } from 'utils/customHooks/useUrlSearchParams';
import { useAddQueryParams } from 'utils/customHooks/useAddQueryParams';

import { AppDispatch, ImmutableRootState } from '../store/store';
import { DATE_TIME_FORMAT } from '../constants/dates';
import {
  describeLocation,
  describeLocationWorkflow,
  LocationsState,
} from '../store/slices/locationsSlice';

type DefaultRange = 'day' | 'week' | 'month';

interface UseVisitsUrlFilterOptions {
  // Default calendar range used to seed the URL when it has no filter yet.
  // The Visitors page uses a single day; report pages use the current week.
  defaultRange?: DefaultRange;
  // Explicit default calendar dates (YYYY-MM-DD) that override `defaultRange`
  // when seeding the URL — e.g. the Visitors page navigation `selectedDate`.
  defaultStartDate?: string;
  defaultEndDate?: string;
}

export interface VisitsUrlFilter {
  // Raw URL values (undefined until the URL is seeded).
  urlLocationId?: string;
  urlStartDate?: string;
  urlEndDate?: string;
  // Effective, parsed values ready to feed into fetchVisits / client filtering.
  // Only defined once `isReady` is true.
  locationId?: number;
  startDate?: string;
  endDate?: string;
  // Calendar dates (YYYY-MM-DD) for the date picker; always defined.
  startDateOnly: string;
  endDateOnly: string;
  // True once the locations list is loaded AND the URL carries the full filter,
  // i.e. a fetch driven by these values is safe and deterministic.
  isReady: boolean;
  // Persist a new filter to the URL (single source of truth). Accepts the same
  // (locationId, startDate, endDate) shape emitted by DateRangeLocationFilter.
  setFilter: (locationId: number, startDate: string, endDate: string) => void;
}

/**
 * Centralizes the URL-driven visit filter (location + date range) shared by the
 * Visitors page and the report pages.
 *
 * Responsibilities:
 * - Seed the URL with a default filter when it's missing, so a hard refresh is
 *   deterministic and shareable.
 * - Restore the global location from the URL exactly once, after the default
 *   global location resolves (describeLocation is takeLatest, so dispatching
 *   ours last makes the URL win; the ref guard prevents fighting later dropdown
 *   changes). This keeps every fetchVisits({ orgId }) caller (drawer actions)
 *   consistent with the URL.
 * - Expose parsed, timezone-correct query bounds so callers can pass them
 *   explicitly to fetchVisits — eliminating the empty-on-refresh race where the
 *   fetch ran before the async global-location load completed.
 */
export const useVisitsUrlFilter = ({
  defaultRange = 'day',
  defaultStartDate,
  defaultEndDate,
}: UseVisitsUrlFilterOptions = {}): VisitsUrlFilter => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = getOpParams();

  const {
    locationId: urlLocationId,
    startDate: urlStartDate,
    endDate: urlEndDate,
  } = useUrlSearchParams();
  const addQueryParams = useAddQueryParams();

  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as NonNullable<LocationsState['globalLocation']>['id'] | undefined;
  const globalLocation = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation']),
  ) as LocationsState['globalLocation'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locationsLoaded = locations.length > 0;

  // Resolve the timezone for a location from the loaded locations list,
  // independent of the (possibly not-yet-resolved) global location.
  const getLocationTz = useCallback(
    (locId: number): string => {
      if (locId === -1) return globalLocation?.timezone || browserTz;
      return (
        locations.find((loc) => loc.id === locId)?.timezone ||
        globalLocation?.timezone ||
        browserTz
      );
    },
    [locations, globalLocation, browserTz],
  );

  // Build the wall-clock query bounds (DATE_TIME_FORMAT) from a calendar date.
  // Mirrors DateRangeLocationFilter.handleUpdate so query behavior is identical:
  // for a specific location the date is anchored in that location's timezone,
  // for "All Locations" (-1) it stays timezone-naive (the saga expands per
  // location).
  const buildQueryStart = useCallback(
    (locId: number, dateOnly: string): string =>
      locId === -1
        ? opTime(dateOnly).startOf('day').format(DATE_TIME_FORMAT)
        : opTime
            .tz(dateOnly, getLocationTz(locId))
            .startOf('day')
            .format(DATE_TIME_FORMAT),
    [getLocationTz],
  );
  const buildQueryEnd = useCallback(
    (locId: number, dateOnly: string): string =>
      locId === -1
        ? opTime(dateOnly).endOf('day').format(DATE_TIME_FORMAT)
        : opTime
            .tz(dateOnly, getLocationTz(locId))
            .endOf('day')
            .format(DATE_TIME_FORMAT),
    [getLocationTz],
  );

  const defaultTz = globalLocation?.timezone || browserTz;
  const nowInTz = opTime().tz(defaultTz);
  const computedStartOnly =
    defaultRange === 'week'
      ? nowInTz.startOf('week').format('YYYY-MM-DD')
      : defaultRange === 'month'
        ? nowInTz.startOf('month').format('YYYY-MM-DD')
        : nowInTz.format('YYYY-MM-DD');
  const computedEndOnly =
    defaultRange === 'week'
      ? nowInTz.endOf('week').format('YYYY-MM-DD')
      : defaultRange === 'month'
        ? nowInTz.endOf('month').format('YYYY-MM-DD')
        : nowInTz.format('YYYY-MM-DD');
  const defaultStartOnly = defaultStartDate ?? computedStartOnly;
  const defaultEndOnly = defaultEndDate ?? computedEndOnly;

  // 1) Seed the URL with the effective filter when it's missing.
  useEffect(() => {
    if (!locationsLoaded) return;
    if (urlLocationId && urlStartDate && urlEndDate) return;
    if (globalLocationId == null) return;

    const params = new URLSearchParams(location.search);
    params.set('locationId', String(globalLocationId));
    params.set('startDate', defaultStartOnly);
    params.set('endDate', defaultEndOnly);
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationsLoaded,
    globalLocationId,
    urlLocationId,
    urlStartDate,
    urlEndDate,
  ]);

  // 2) One-time restore of the global location from the URL, after the default
  //    global location resolves.
  const restoredLocationRef = useRef(false);
  useEffect(() => {
    if (restoredLocationRef.current) return;
    if (!locationsLoaded || globalLocationId == null) return;
    restoredLocationRef.current = true;

    if (!urlLocationId) return;
    const locId = Number(urlLocationId);
    if (Number.isNaN(locId) || locId === globalLocationId) return;
    dispatch(describeLocation({ orgId, locationId: locId, global: true }));
    dispatch(
      describeLocationWorkflow({ orgId, locationId: locId, global: true }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsLoaded, globalLocationId, urlLocationId, orgId]);

  const setFilter = useCallback(
    (locationId: number, startDate: string, endDate: string) => {
      addQueryParams({
        locationId: String(locationId),
        startDate: opTime(startDate).format('YYYY-MM-DD'),
        endDate: opTime(endDate).format('YYYY-MM-DD'),
      });
    },
    [addQueryParams],
  );

  const hasUrlFilter = Boolean(urlLocationId && urlStartDate && urlEndDate);
  const isReady = locationsLoaded && hasUrlFilter;
  const locationId =
    urlLocationId != null && urlLocationId !== ''
      ? Number(urlLocationId)
      : undefined;
  const startDate =
    isReady && locationId != null
      ? buildQueryStart(locationId, urlStartDate as string)
      : undefined;
  const endDate =
    isReady && locationId != null
      ? buildQueryEnd(locationId, urlEndDate as string)
      : undefined;

  return {
    urlLocationId,
    urlStartDate,
    urlEndDate,
    locationId,
    startDate,
    endDate,
    startDateOnly: urlStartDate || defaultStartOnly,
    endDateOnly: urlEndDate || defaultEndOnly,
    isReady,
    setFilter,
  };
};
